import { useSignals } from '@preact/signals-react/runtime';
import type { Signal } from '@preact/signals-react';
import { useState } from 'react';
import { ArrowLeft, Maximize2, Minimize2, X } from 'lucide-react';
import type { AppParams, FeatureKey } from '../types';
import type { FeaturePayload } from '../api/feature-payload';
import { IconButton } from '../components/IconButton';
import { Drawer } from './Drawer';
import { FAB } from './FAB';
import { PhiaWordmark } from './PhiaWordmark';
import { FEATURE_BY_KEY } from './features-catalog';
import { HomeView } from './HomeView';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { ApplicationsView } from '../features/applications/ApplicationsView';
import { BibliographyView } from '../features/bibliography/BibliographyView';
import { DiagramView } from '../features/diagram/DiagramView';
import { DiscoverView } from '../features/discover/DiscoverView';
import { ExpandView } from '../features/expand/ExpandView';
import { ExplainView } from '../features/explain/ExplainView';
import { FlashcardsView } from '../features/flashcards/FlashcardsView';
import { GlossaryView } from '../features/glossary/GlossaryView';
import { GoalsView } from '../features/goals/GoalsView';
import { PlaceholderView } from '../features/PlaceholderView';
import { PodcastsView } from '../features/podcasts/PodcastsView';
import { QuizView } from '../features/quiz/QuizView';
import { RegulationView } from '../features/regulation/RegulationView';
import { RememberView } from '../features/remember/RememberView';
import { SummaryView } from '../features/summary/SummaryView';
import { ThreadsView } from '../features/threads/ThreadsView';
import { UnderlineView } from '../features/underline/UnderlineView';
import styles from './WidgetShell.module.css';

type Props = {
  paramsSignal: Signal<AppParams | null>;
  openFeatureSignal: Signal<FeatureKey | null>;
  drawerOpenSignal: Signal<boolean>;
  featurePayloadSignal: Signal<FeaturePayload | null>;
  shadowRoot: ShadowRoot;
};

export function WidgetShell({
  paramsSignal,
  openFeatureSignal,
  drawerOpenSignal,
  featurePayloadSignal,
  shadowRoot
}: Props) {
  useSignals();
  const params = paramsSignal.value;
  const active = openFeatureSignal.value;
  const isOpen = drawerOpenSignal.value;
  const payload = featurePayloadSignal.value;

  // Visual fullscreen — the drawer fills the viewport via CSS only.
  // We never call the browser's Fullscreen API (F11-style) from this
  // widget: it's too intrusive for a side panel and was the bug behind
  // "opening the drawer puts the whole browser in fullscreen".
  const [isFullscreen, setIsFullscreen] = useState(false);

  if (!params) return null;

  const toggleFullscreen = () => setIsFullscreen((v) => !v);

  // Re-open the drawer to wherever the user left off: if a feature was
  // active when they closed, it's still mounted and just becomes visible
  // again.
  const openWidget = () => {
    drawerOpenSignal.value = true;
  };

  const openFeature = (key: FeatureKey, p?: unknown) => {
    featurePayloadSignal.value = (p as FeaturePayload) ?? null;
    openFeatureSignal.value = key;
    drawerOpenSignal.value = true;
  };

  const goBackHome = () => {
    openFeatureSignal.value = null;
    featurePayloadSignal.value = null;
  };

  // Closing the drawer only hides it. The active feature stays mounted
  // so its React state (chat history, deck position, etc.) survives until
  // the widget itself unmounts.
  const close = () => {
    drawerOpenSignal.value = false;
  };

  const meta = active ? FEATURE_BY_KEY[active] : null;

  return (
    <>
      <FAB onClick={openWidget} hidden={isOpen} />
      <Drawer
        open={isOpen}
        onClose={close}
        shadowRoot={shadowRoot}
        mode={params.displayMode ?? 'push'}
        fullscreen={isFullscreen}
        headerLeft={
          meta ? (
            <>
              <IconButton label="Volver" onClick={goBackHome} size="md">
                <ArrowLeft size={18} />
              </IconButton>
              <span className={styles.title}>{meta.label}</span>
            </>
          ) : (
            <PhiaWordmark className={styles.wordmark} />
          )
        }
        headerActions={
          <>
            <IconButton
              label={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
              onClick={toggleFullscreen}
              size="md"
            >
              {isFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
            </IconButton>
            <IconButton label="Cerrar" onClick={close} size="md">
              <X size={18} />
            </IconButton>
          </>
        }
      >
        {meta ? (
          <ErrorBoundary key={meta.key}>
            <FeatureRouter feature={meta.key} payload={payload} />
          </ErrorBoundary>
        ) : (
          <HomeView onOpenFeature={openFeature} />
        )}
      </Drawer>
    </>
  );
}

function FeatureRouter({ feature, payload }: { feature: FeatureKey; payload?: unknown }) {
  switch (feature) {
    case 'threads':
      return <ThreadsView initialMessage={(payload as { initialMessage?: string })?.initialMessage} />;
    case 'flashcards':
      return <FlashcardsView />;
    case 'quiz':
      return <QuizView />;
    case 'summary':
      return <SummaryView />;
    case 'podcasts':
      return <PodcastsView />;
    case 'diagram':
      return <DiagramView />;
    case 'bibliography':
      return <BibliographyView />;
    case 'regulation':
      return <RegulationView />;
    case 'glossary':
      return <GlossaryView />;
    case 'goals':
      return <GoalsView />;
    case 'applications':
      return <ApplicationsView />;
    case 'expand':
      return <ExpandView />;
    case 'explain':
      return <ExplainView />;
    case 'remember':
      return <RememberView />;
    case 'discover':
      return <DiscoverView />;
    case 'underline':
      return <UnderlineView />;
    default:
      return <PlaceholderView feature={feature} />;
  }
}
